import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import cn from 'classnames';
import {
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  REDO_COMMAND,
  UNDO_COMMAND,
  type LexicalEditor,
  type NodeKey,
} from 'lexical';
import { isEqual } from 'lodash-es';
import {
  createContext,
  CSSProperties,
  FC,
  forwardRef,
  Fragment,
  memo,
  MutableRefObject,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { NoteList } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import { Icon } from '@/shared/ui';

import {
  $bracketTemplateFieldInstruction,
  $editTemplateOptions,
  $insertTemplateBlockAtCaret,
  $insertTemplateFieldSection,
  $isTemplateFieldNode,
  $isTemplateHeadingNode,
  $isTemplateInstructionNode,
  $isSectionGuidance,
  $isTemplateParagraphNode,
  $readCaretBlockKey,
  $readSelectedTemplateLineIds,
  $readSelectedTemplateText,
  $readTemplateLine,
  $readTemplateSection,
  $removeTemplateBlock,
  $removeTemplateSection,
  $selectAfterTemplateBlock,
  $selectTemplateBlock,
  $selectTemplateLine,
  $selectTemplateSection,
  $selectTemplateValue,
  $setTemplateBlockDef,
  $turnCaretIntoTemplateHeading,
  $turnCaretIntoTemplateLine,
  $updateTemplateSectionById,
  createNoteBlockStore,
  createTemplateSlotDef,
  isScoredSection,
  listNoteBlockOptions,
  markTemplateHeadingTag,
  noteChoiceLabel,
  normalizeDocumentType,
  noteOptionValue,
  START_TEMPLATE_LINK_COMMAND,
  TEMPLATE_INSTRUCTION_TYPED_COMMAND,
  TEMPLATE_LINK_ADAPTER,
  TemplateEditor,
  useNoteBlockEntry,
  useTemplateLineLinkEditing,
  type DocumentSectionSpec,
  type DocumentTemplate,
  type NoteBlockStore,
  type NoteChoiceValuesEdit,
  type TemplateDocumentExport,
  type TemplateEditorHistory,
  type TemplateFieldWidget,
  type TemplateFieldWidgets,
  type TemplateIssue,
} from '@/entities/templates';

import {
  i18n,
  type DocumentHistory,
  type TemplateDocumentFocus,
} from '../config';
import { useCaretJump, useTemplateIssueText } from '../hooks';
import {
  applyTemplateDocument,
  buildFieldContour,
  createListField,
  createNoteBlock,
  createNoteSection,
  describeNoteLine,
  findStructureIssues,
  getNoteBlockLabel,
  getNoteBlockPaletteKind,
  getNoteContextKeyMessage,
  listNoteLineOpeners,
  listNoteOptionLines,
  listShownLineOpeners,
  listUnnamedNoteFields,
  nameNoteLinkOpener,
  normalizeNoteValue,
  placeTemplateIssues,
  findLinkOption,
  revealLinkTarget,
  restoreStoredFieldOrder,
  showTranscriptQualityFirst,
  toContourRows,
  withFieldCodes,
  type FieldContour,
  type NoteBlockDef,
  type NoteBlockPaletteKind,
  type NamedNoteLinkOpener,
  type NoteLinkOpener,
  type PlacedTemplateIssues,
  type StructureIssue,
} from '../lib';

import {
  FieldPopupSettings,
  SectionPopupSettings,
  SlotPopupSettings,
  TemplateIssues,
} from './FieldSettings';
import { LinkedLineMark, LinkRow } from './LinkedLineMark';
import { SettingsPopover } from './SettingsPopover';
import styles from './TemplateDocumentEditor.module.scss';

type BlockEntry = { def: NoteBlockDef; issues: TemplateIssue[] };

type LineEntry = {
  // The options that open the line.
  openers: NoteLinkOpener[];
  // Its own issues and those of its wording, which has no frame of its own.
  issues: TemplateIssue[];
};

type SectionEntry = {
  title: string;
  rewrite: boolean;
  required: boolean;
  issues: TemplateIssue[];
  // A field of a summary or an evaluation: the code its text is kept
  // under, its title in the saved version, whether AI scores it, and what
  // keeps the list from saving.
  code: string;
  savedTitle?: string;
  scored: boolean;
  fieldIssues: StructureIssue['kind'][];
};

type Listener = () => void;

// One value the editor's widgets read; each re-renders only when what it
// selects of it changes.
type ValueStore<T> = {
  get: () => T;
  set: (next: T) => void;
  subscribe: (listener: Listener) => () => void;
};

const createValueStore = <T,>(initial: T): ValueStore<T> => {
  const state = { value: initial };
  const listeners = new Set<Listener>();
  return {
    get: () => state.value,
    set: (next) => {
      if (!isEqual(next, state.value)) {
        state.value = next;
        listeners.forEach((listener) => listener());
      }
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

const useStoreValue = <T, S>(store: ValueStore<T>, select: (value: T) => S) =>
  useSyncExternalStore(store.subscribe, () => select(store.get()));

// The field and the line or heading the tools are shown for: the one under
// the pointer, else the one that holds the focus or the caret; one whose
// pop-up is open holds on.
type ActivePins = { field: NodeKey | null; block: NodeKey | null };

// `held` is the field that holds the focus or the caret, is selected, or
// has its pop-up open: the one outlined.
type ActiveTarget = ActivePins & { held: NodeKey | null };

// What a widget asks for once it renders: a new choice's first option
// typed, a new table's settings open, a new instruction's text typed.
type WidgetRequest = {
  blockId: string;
  kind: 'options' | 'settings' | 'text';
} | null;

type WidgetScope = {
  blocks: NoteBlockStore<BlockEntry>;
  lines: NoteBlockStore<LineEntry>;
  // The lines each value of a choice block opens, by `optionKey`.
  options: NoteBlockStore<string[]>;
  sections: NoteBlockStore<SectionEntry>;
  active: ValueStore<ActiveTarget>;
  pins: ValueStore<ActivePins>;
  request: ValueStore<WidgetRequest>;
  // The template as last read from the document, for what a widget names
  // across it: link triggers and the lines an option opens.
  latest: MutableRefObject<DocumentTemplate>;
  lists: NoteList[];
  editable: boolean;
  // A summary's or an evaluation's fields: a section is a field, with no
  // lines.
  fields: boolean;
  // Only the fields' titles and instructions change, as before a recording.
  structureLocked: boolean;
};

const WidgetScopeContext = createContext<WidgetScope | null>(null);

const useWidgetScope = () => {
  const scope = useContext(WidgetScopeContext);
  if (!scope) {
    throw new Error('A template widget renders outside TemplateDocumentEditor');
  }
  return scope;
};

const useIsActive = (nodeKey: NodeKey) =>
  useStoreValue(useWidgetScope().active, (active) => active.field === nodeKey);

const useIsHeld = (nodeKey: NodeKey) =>
  useStoreValue(useWidgetScope().active, (active) => active.held === nodeKey);

const useRequest = (blockId: string) =>
  useStoreValue(useWidgetScope().request, (request) =>
    request?.blockId === blockId ? request.kind : null,
  );

const NO_ISSUES: TemplateIssue[] = [];

const listBlockEntries = (
  template: DocumentTemplate,
  placed: PlacedTemplateIssues,
): Record<string, BlockEntry> =>
  Object.fromEntries(
    template.sections.flatMap((section) =>
      section.Lines.flatMap((line) => {
        const unnamed = listUnnamedNoteFields(line);
        return line.Blocks.map((def) => {
          const issues = [
            ...(placed.byBlock.get(def.ID) ?? []),
            ...(unnamed.includes(def.ID)
              ? [
                  {
                    Kind: 'warning_unnamed_field',
                    SectionID: section.ID,
                    LineID: line.ID,
                    BlockID: def.ID,
                    Message: '',
                  },
                ]
              : []),
          ];
          return [def.ID, { def, issues: issues.length ? issues : NO_ISSUES }];
        });
      }),
    ),
  );

const listLineEntries = (
  template: DocumentTemplate,
  placed: PlacedTemplateIssues,
): Record<string, LineEntry> =>
  Object.fromEntries(
    template.sections.flatMap((section) =>
      section.Lines.map((line) => [
        line.ID,
        {
          openers: listNoteLineOpeners(template, line.ID),
          issues: [
            ...(placed.byLine.get(line.ID) ?? []),
            ...line.Blocks.filter(
              (block) => block.Kind.case === 'Text',
            ).flatMap((block) => placed.byBlock.get(block.ID) ?? []),
          ],
        },
      ]),
    ),
  );

const optionKey = (blockId: string, value: string) => `${blockId}\n${value}`;

const listOptionEntries = (
  template: DocumentTemplate,
): Record<string, string[]> =>
  Object.fromEntries(
    template.sections.flatMap((section) =>
      section.Lines.flatMap((line) =>
        line.Blocks.flatMap((block) =>
          block.Kind.case === 'Choice' || block.Kind.case === 'MultiChoice'
            ? [
                ...block.Kind.value.Values,
                ...listNoteBlockOptions(block).map(noteOptionValue),
              ].map((value) => [
                optionKey(block.ID, value),
                listNoteOptionLines(template, block.ID, value),
              ])
            : [],
        ),
      ),
    ),
  );

const listSectionEntries = (
  template: DocumentTemplate,
  source: DocumentTemplate,
  placed: PlacedTemplateIssues,
  fields: boolean,
): Record<string, SectionEntry> => {
  const fieldIssues = fields ? findStructureIssues(template) : [];
  const documentType = normalizeDocumentType(template.type);
  return Object.fromEntries(
    template.sections.map((section, index) => [
      section.ID,
      {
        title: section.Title,
        rewrite: !!section.AllowAITextRewrite,
        required: section.Required,
        issues: placed.bySection.get(section.ID) ?? NO_ISSUES,
        code: section.Code,
        savedTitle: source.sections.find((item) => item.Code === section.Code)
          ?.Title,
        scored: fields && isScoredSection(documentType, section),
        fieldIssues: fieldIssues.flatMap((issue) =>
          issue.section === index ? [issue.kind] : [],
        ),
      },
    ]),
  );
};

const listLineIds = (template: DocumentTemplate) =>
  template.sections.flatMap((section) => section.Lines.map((line) => line.ID));

// A jump along a link: to a line an option opens, or back to the option
// from the line, lit on arrival as in the note.
// The caret goes on right after a field or an instruction, out of the box
// it was typed in: the document takes the focus back, as a jump does.
const useLeaveBlock = (nodeKey: NodeKey) => {
  const jump = useCaretJump();
  return () =>
    jump(() => {
      $selectAfterTemplateBlock(nodeKey);
      return [];
    });
};

const useLinkJump = () => {
  const jump = useCaretJump();
  return {
    toLine: (lineId: string) =>
      revealLinkTarget(jump(() => [$selectTemplateLine(lineId)])),
    toOption: ({ blockId, value }: NoteLinkOpener) => {
      const [line] = jump(() => [$selectTemplateValue(blockId)]);
      revealLinkTarget([line, findLinkOption(line, blockId, value)]);
    },
  };
};

type OptionLinksProps = {
  opens: string[];
  blockId: string;
  value: string;
  onDone: () => void;
};

type OptionLinkLineProps = Omit<OptionLinksProps, 'opens'> & {
  lineId: string;
};

// A line an option opens, as the line's own «↳ option» pop-up lists the
// option: it leads to the line, and its ✎ and ✕ change or drop the line's
// link to this option.
const OptionLinkLine: FC<OptionLinkLineProps> = ({
  lineId,
  blockId,
  value,
  onDone,
}) => {
  const scope = useWidgetScope();
  const { formatMessage } = useIntl();
  const linkJump = useLinkJump();
  const linkEditing = useTemplateLineLinkEditing(TEMPLATE_LINK_ADAPTER, lineId);
  const line = scope.latest.current.sections
    .flatMap((section) => section.Lines)
    .find((item) => item.ID === lineId);
  const label = line
    ? describeNoteLine(line) ||
      formatMessage({
        id: 'workspace.templateEdit.emptyLine',
        defaultMessage: 'Empty line',
      })
    : lineId;

  const act = (action: () => void) => {
    onDone();
    action();
  };

  return (
    <LinkRow
      label={label}
      dataComponent="workspace-template-option-link-line"
      onSelect={() => act(() => linkJump.toLine(lineId))}
      onChange={() => act(() => linkEditing.onReplace({ blockId, value }))}
      onRemove={() => act(() => linkEditing.onRemove({ blockId, value }))}
    />
  );
};

// What an option's chain opens: the lines the option opens.
const OptionLinksMenu: FC<OptionLinksProps> = ({ opens, ...option }) => (
  <span
    className={styles.optionLinks}
    data-component="workspace-template-option-links"
  >
    <span className={styles.optionLinksPart}>
      <span className={cn('p4', styles.optionLinksTitle)}>
        <FormattedMessage
          id="workspace.templateEdit.linkedLines"
          defaultMessage="Linked lines"
        />
      </span>
      {opens.map((lineId) => (
        <OptionLinkLine key={lineId} lineId={lineId} {...option} />
      ))}
    </span>
  </span>
);

// After a value of a choice that opens lines: its chain, which leads to
// them.
const OptionTools: FC<{
  nodeKey: NodeKey;
  blockId: string;
  value: string;
  active: boolean;
}> = ({ nodeKey, blockId, value, active }) => {
  const scope = useWidgetScope();
  const lines = useNoteBlockEntry(scope.options, optionKey(blockId, value));
  const { formatMessage } = useIntl();
  const linkJump = useLinkJump();
  const [open, setOpen] = useState(false);
  // A jump takes the focus to the line; otherwise it goes back to the chain.
  const jumped = useRef(false);
  const opens = lines ?? [];
  if (!opens.length) {
    return null;
  }
  const opensLabel = formatMessage(
    {
      id: 'workspace.note.linkOpens',
      defaultMessage: 'opens {count, plural, one {# line} other {# lines}}',
    },
    { count: opens.length },
  );

  const changeOpen = (next: boolean) => {
    setOpen(next);
    scope.pins.set({ ...scope.pins.get(), field: next ? nodeKey : null });
  };

  if (!scope.editable || (!active && !open)) {
    return (
      <button
        type="button"
        className={styles.optionLink}
        aria-label={opensLabel}
        title={opensLabel}
        data-component="workspace-template-option-opens"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => linkJump.toLine(opens[0])}
      >
        <Icon name="link" size={14} />
      </button>
    );
  }

  return (
    <SettingsPopover
      portal
      label={opensLabel}
      icon="link"
      iconSize={14}
      className={styles.optionLink}
      open={open}
      dataComponent="workspace-template-option-opens"
      onOpenChange={changeOpen}
      onCloseAutoFocus={(event) => {
        if (jumped.current) {
          event.preventDefault();
          jumped.current = false;
        }
      }}
    >
      <OptionLinksMenu
        opens={opens}
        blockId={blockId}
        value={value}
        onDone={() => {
          jumped.current = true;
          changeOpen(false);
        }}
      />
    </SettingsPopover>
  );
};

// A field's issues, as the warning of a field with no name: a mark in its
// frame's corner, read in its tooltip.
const FieldIssueMark: FC<{ blockId: string }> = ({ blockId }) => {
  const scope = useWidgetScope();
  const entry = useNoteBlockEntry(scope.blocks, blockId);
  const { describe } = useTemplateIssueText();
  const issues = entry?.issues ?? NO_ISSUES;
  if (!issues.length) {
    return null;
  }
  const text = issues.map(describe).join('\n');
  return (
    <span
      role="img"
      className={cn(
        styles.fieldIssue,
        issues.every((issue) => issue.Kind.startsWith('warning_')) &&
          styles.fieldWarning,
      )}
      aria-label={text}
      title={text}
      data-component="workspace-template-field-issue"
    >
      <Icon name="attention" size={12} />
    </span>
  );
};

// The button at an active field's top-right corner: its pop-up opens above
// the field, with the few settings the editor shows of it.
const FieldMenu: FC<{
  nodeKey: NodeKey;
  def: NoteBlockDef;
  corner: FieldContour['corner'] | undefined;
}> = ({ nodeKey, def, corner }) => {
  const scope = useWidgetScope();
  const [editor] = useLexicalComposerContext();
  const { formatMessage } = useIntl();
  const active = useIsActive(nodeKey);
  const request = useRequest(def.ID);
  const [open, setOpen] = useState(false);
  const changeOpen = (next: boolean) => {
    setOpen(next);
    scope.pins.set({ ...scope.pins.get(), field: next ? nodeKey : null });
  };

  useEffect(() => {
    if (request === 'settings') {
      scope.request.set(null);
      changeOpen(true);
    }
    // A request is followed once, when it is made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  if (!scope.editable || (!active && !open)) {
    return null;
  }

  return (
    <span
      className={styles.corner}
      style={corner && { left: corner.x, top: corner.y }}
    >
      <SettingsPopover
        portal
        label={formatMessage({
          id: 'workspace.template.fieldSettings',
          defaultMessage: 'Field settings',
        })}
        side="top"
        iconSize={12}
        className={styles.cornerButton}
        open={open}
        dataComponent="workspace-template-field-menu"
        onOpenChange={changeOpen}
      >
        <FieldPopupSettings
          block={def}
          onChange={(block) =>
            editor.update(() => $setTemplateBlockDef(nodeKey, block))
          }
          onRemove={() => {
            changeOpen(false);
            editor.update(() => $removeTemplateBlock(nodeKey));
          }}
        />
      </SettingsPopover>
    </span>
  );
};

type FieldProps = { nodeKey: NodeKey; def: NoteBlockDef };

const measureFieldContour = (element: HTMLElement) => {
  const origin = element.offsetParent?.getBoundingClientRect();
  return origin
    ? buildFieldContour(
        toContourRows(Array.from(element.getClientRects()), origin),
      )
    : null;
};

// The outline of a field's content while it is shown, measured again after
// every change of the document, of the field's boxes and of the page's
// size; it is drawn relative to the field's line, so a scroll moves both.
const useFieldContour = (
  field: RefObject<HTMLElement>,
  shown: boolean,
): FieldContour | null => {
  const [editor] = useLexicalComposerContext();
  const [contour, setContour] = useState<FieldContour | null>(null);
  const measure = () => {
    const element = field.current;
    const next = shown && element ? measureFieldContour(element) : null;
    setContour((current) => (isEqual(current, next) ? current : next));
  };

  // A field's own render changes what it holds: its options and boxes.
  useLayoutEffect(measure);

  useEffect(() => {
    const element = field.current;
    const root = editor.getRootElement();
    if (!shown || !element || !root) {
      return undefined;
    }
    // A typed box grows after its input event reaches the field.
    const measureLater = () => window.requestAnimationFrame(measure);
    const observer = new ResizeObserver(measureLater);
    observer.observe(root);
    element.addEventListener('input', measureLater);
    return mergeRegister(
      editor.registerUpdateListener(measureLater),
      () => observer.disconnect(),
      () => element.removeEventListener('input', measureLater),
    );
    // `measure` reads the field and `shown` when it runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, field, shown]);

  return contour;
};

// A field reads as the note's text: its content flows on in the line and
// wraps with it. While it is active its settings sit at its first row's
// top-right corner, and while it is held one outline follows its rows.
const FieldFrame: FC<
  FieldProps & {
    className?: string;
    dataComponent: string;
    children: ReactNode;
  }
> = ({ nodeKey, def, className, dataComponent, children }) => {
  const field = useRef<HTMLSpanElement>(null);
  const active = useIsActive(nodeKey);
  const held = useIsHeld(nodeKey);
  const contour = useFieldContour(field, active || held);

  return (
    <span
      ref={field}
      className={cn(styles.field, className)}
      data-component={dataComponent}
    >
      <FieldIssueMark blockId={def.ID} />
      {children}
      {held && contour && (
        <svg
          className={styles.contour}
          aria-hidden
          data-component="workspace-template-field-contour"
        >
          <path d={contour.path} />
        </svg>
      )}
      <FieldMenu nodeKey={nodeKey} def={def} corner={contour?.corner} />
    </span>
  );
};

const readValues = (def: NoteBlockDef) =>
  def.Kind.case === 'Choice' || def.Kind.case === 'MultiChoice'
    ? def.Kind.value.Values
    : [];

// A box sized to what it holds, at least to its placeholder.
const fitBox = (input: HTMLInputElement) => {
  input.size = Math.max(input.value.length, input.placeholder.length, 1) + 1;
};

type OptionInputProps = {
  value: string;
  placeholder: string;
  label: string;
  readOnly: boolean;
  dataComponent: string;
  onCommit: (value: string) => void;
  onEnter: () => void;
  onRemoveEmpty: () => void;
  onLeave: () => void;
};

// One option, typed in a box of its own: Enter goes on to the next option,
// Backspace in an empty one removes it, Escape takes the typing back.
const OptionInput = forwardRef<HTMLInputElement, OptionInputProps>(
  (
    {
      value,
      placeholder,
      label,
      readOnly,
      dataComponent,
      onCommit,
      onEnter,
      onRemoveEmpty,
      onLeave,
    },
    ref,
  ) => (
    <input
      ref={ref}
      className={styles.optionInput}
      defaultValue={value}
      placeholder={placeholder}
      aria-label={label}
      readOnly={readOnly}
      size={Math.max(value.length, placeholder.length, 1) + 1}
      data-component={dataComponent}
      onInput={(event) => fitBox(event.currentTarget)}
      onBlur={(event) => onCommit(event.currentTarget.value.trim())}
      onKeyDown={(event) => {
        const input = event.currentTarget;
        if (event.key === 'Enter') {
          event.preventDefault();
          onCommit(input.value.trim());
          onEnter();
        }
        if (event.key === 'Backspace' && !input.value) {
          event.preventDefault();
          onRemoveEmpty();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          input.value = value;
          onLeave();
        }
      }}
    />
  ),
);

OptionInput.displayName = 'OptionInput';

// The box a new option is typed in, in place of "+ option": Enter adds the
// option and leaves the box empty for the next; Escape, or leaving it
// empty, ends the typing with nothing added.
const NewOptionChip: FC<{
  onAdd: (value: string) => void;
  onEnd: (focus: 'last' | 'editor' | 'none') => void;
}> = ({ onAdd, onEnd }) => {
  const { formatMessage } = useIntl();
  const placeholder = formatMessage({
    id: 'workspace.template.addOptionShort',
    defaultMessage: '+ option',
  });
  const ended = useRef(false);
  const box = useRef<HTMLInputElement>(null);

  useEffect(() => {
    box.current?.focus({ preventScroll: true });
  }, []);

  const end = (focus: 'last' | 'editor' | 'none') => {
    ended.current = true;
    onEnd(focus);
  };

  return (
    <input
      ref={box}
      className={cn(styles.optionInput, styles.newOption)}
      aria-label={formatMessage({
        id: 'workspace.blockEditor.newOption',
        defaultMessage: 'New option',
      })}
      placeholder={placeholder}
      size={placeholder.length + 1}
      data-component="workspace-template-new-option"
      onInput={(event) => fitBox(event.currentTarget)}
      onBlur={(event) => {
        if (!ended.current && event.currentTarget.value.trim()) {
          onAdd(event.currentTarget.value.trim());
        }
        if (!ended.current) {
          end('none');
        }
      }}
      onKeyDown={(event) => {
        const input = event.currentTarget;
        if (event.key === 'Enter') {
          event.preventDefault();
          if (!input.value.trim()) {
            end('editor');
            return;
          }
          onAdd(input.value.trim());
          input.value = '';
          fitBox(input);
        }
        if (event.key === 'Backspace' && !input.value) {
          event.preventDefault();
          end('last');
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          end('editor');
        }
      }}
    />
  );
};

// A choice: its options typed in the field's own boxes, joined as the
// sentence reads them, and "+ option" after them, all in one frame.
const ChoiceField: FC<FieldProps> = ({ nodeKey, def }) => {
  const scope = useWidgetScope();
  const [editor] = useLexicalComposerContext();
  const { formatMessage } = useIntl();
  const active = useIsActive(nodeKey);
  const request = useRequest(def.ID);
  const [adding, setAdding] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  // The option the focus goes to once the field shows its new options.
  const pendingFocus = useRef<number | null>(null);
  const values = readValues(def);
  const placeholder = formatMessage({
    id: 'workspace.templateEdit.optionPlaceholder',
    defaultMessage: 'option',
  });
  const optionLabel = formatMessage({
    id: 'workspace.templateEdit.option',
    defaultMessage: 'Option',
  });
  const addOptionLabel = formatMessage({
    id: 'workspace.blockEditor.addOption',
    defaultMessage: 'Add option',
  });

  useEffect(() => {
    if (request === 'options') {
      scope.request.set(null);
      setAdding(true);
    }
    // A request is followed once, when it is made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  useEffect(() => {
    const index = pendingFocus.current;
    if (index === null) {
      return;
    }
    pendingFocus.current = null;
    const input = inputs.current[index];
    input?.focus({ preventScroll: true });
    input?.setSelectionRange(input.value.length, input.value.length);
  });

  const edit = (change: NoteChoiceValuesEdit) =>
    editor.update(() => $editTemplateOptions(nodeKey, change));
  const leave = useLeaveBlock(nodeKey);
  // A value the field holds already is not added twice.
  const add = (value: string) => {
    if (
      !values.some(
        (held) => normalizeNoteValue(held) === normalizeNoteValue(value),
      )
    ) {
      edit({ type: 'add', value });
    }
  };

  return (
    <FieldFrame
      nodeKey={nodeKey}
      def={def}
      className={styles.choice}
      dataComponent="workspace-template-options"
    >
      {values.map((value, index) => (
        <Fragment key={`${index}:${value}`}>
          <span
            className={styles.option}
            data-link-block={def.ID}
            data-link-value={value}
            data-link-label={value}
          >
            <OptionInput
              ref={(input) => {
                inputs.current[index] = input;
              }}
              value={value}
              placeholder={placeholder}
              label={optionLabel}
              readOnly={!scope.editable}
              dataComponent="workspace-template-option"
              onCommit={(next) => {
                if (next && next !== value) {
                  edit({ type: 'rename', index, value: next });
                }
              }}
              onEnter={() => {
                if (index < values.length - 1) {
                  inputs.current[index + 1]?.focus();
                } else {
                  setAdding(true);
                }
              }}
              onRemoveEmpty={() => {
                pendingFocus.current =
                  values.length > 1 ? Math.max(index - 1, 0) : null;
                edit({ type: 'remove', index });
              }}
              onLeave={leave}
            />
            <OptionTools
              nodeKey={nodeKey}
              blockId={def.ID}
              value={value}
              active={active}
            />
          </span>
          <wbr />
        </Fragment>
      ))}
      {scope.editable &&
        (adding ? (
          <NewOptionChip
            onAdd={add}
            onEnd={(focus) => {
              setAdding(false);
              if (focus === 'last' && values.length) {
                pendingFocus.current = values.length - 1;
              }
              if (focus === 'editor') {
                leave();
              }
            }}
          />
        ) : (
          <button
            type="button"
            className={styles.addOption}
            aria-label={addOptionLabel}
            title={addOptionLabel}
            data-component="workspace-template-add-option"
            onClick={() => setAdding(true)}
          >
            <FormattedMessage
              id="workspace.template.addOptionShort"
              defaultMessage="+ option"
            />
          </button>
        ))}
    </FieldFrame>
  );
};

// A choice whose options carry codes, print forms, hints or pictures: its
// options read as they are (a diagnosis after its code) and are kept as
// stored; a line one of them opens is added from it under the value scribe
// stores for it.
const OptionsBlock: FC<FieldProps> = ({ nodeKey, def }) => {
  const active = useIsActive(nodeKey);
  return (
    <FieldFrame
      nodeKey={nodeKey}
      def={def}
      className={styles.optionSet}
      dataComponent="workspace-template-option-set"
    >
      {listNoteBlockOptions(def).map((option) => {
        const value = noteOptionValue(option);
        const label = noteChoiceLabel(def, value);
        return (
          <Fragment key={value}>
            <span
              className={styles.setOption}
              data-link-block={def.ID}
              data-link-value={value}
              data-link-label={label}
            >
              <span className={styles.optionLabel}>{label}</span>
              <OptionTools
                nodeKey={nodeKey}
                blockId={def.ID}
                value={value}
                active={active}
              />
            </span>
            <wbr />
          </Fragment>
        );
      })}
    </FieldFrame>
  );
};

const describeBlock = (
  def: NoteBlockDef,
  lists: NoteList[],
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => {
  if (def.Kind.case === 'Context') {
    return formatMessage(getNoteContextKeyMessage(def.Kind.value.Key));
  }
  return (
    getNoteBlockLabel(def, lists) ??
    formatMessage(i18n.noteBlockKindMessages[getNoteBlockPaletteKind(def)])
  );
};

// Teeth, a date, a number, a table, a record value and a list block: the
// field's name, underlined as the slot the note fills.
const ValueBlock: FC<FieldProps> = ({ nodeKey, def }) => {
  const scope = useWidgetScope();
  const { formatMessage } = useIntl();
  return (
    <FieldFrame
      nodeKey={nodeKey}
      def={def}
      className={styles.value}
      dataComponent="workspace-template-value"
    >
      <span className={styles.valueName}>
        {describeBlock(def, scope.lists, formatMessage)}
      </span>
    </FieldFrame>
  );
};

// A kind this client does not know is kept as stored and never changed; it
// shows its label when it has one.
const UnknownBlock: FC<FieldProps> = ({ def }) => {
  const label = (def.Kind.value as { Label?: string } | undefined)?.Label;
  return (
    <span
      className={cn(styles.field, styles.value)}
      data-component="workspace-template-unknown-block"
    >
      {label ?? (
        <FormattedMessage
          id="workspace.templateEdit.unknownBlock"
          defaultMessage="A block this version cannot show"
        />
      )}
    </span>
  );
};

const FieldBody: FC<FieldProps> = ({ nodeKey, def }) => {
  switch (def.Kind.case) {
    case 'Choice':
    case 'MultiChoice':
      return listNoteBlockOptions(def).length ? (
        <OptionsBlock nodeKey={nodeKey} def={def} />
      ) : (
        <ChoiceField nodeKey={nodeKey} def={def} />
      );
    case 'ListRef':
    case 'Teeth':
    case 'Context':
    case 'Date':
    case 'Number':
    case 'Table':
      return <ValueBlock nodeKey={nodeKey} def={def} />;
    default:
      return <UnknownBlock nodeKey={nodeKey} def={def} />;
  }
};

// The text that names a field: the run marked as its name right before it.
const readFieldName = (element: Element | null) => {
  const field = element?.closest('[data-template-field]');
  const siblings = Array.from(field?.parentElement?.children ?? []);
  const before = siblings.slice(0, siblings.indexOf(field!)).reverse();
  const end = before.findIndex(
    (sibling) => !sibling.hasAttribute('data-field-name'),
  );
  return before
    .slice(0, end < 0 ? before.length : end)
    .reverse()
    .map((sibling) => sibling.textContent ?? '')
    .join('')
    .trim()
    .replace(/:$/, '');
};

// A field's AI instruction, after its frame: guidance on how the AI fills
// the field, never printed. It goes with the field; emptied, it goes.
const FieldGuidance: FC<FieldProps> = ({ nodeKey, def }) => {
  const [editor] = useLexicalComposerContext();
  const { formatMessage } = useIntl();
  const leave = useLeaveBlock(nodeKey);
  const [name, setName] = useState('');
  const setInstruction = (AIInstruction: string | undefined) =>
    editor.update(() =>
      $setTemplateBlockDef(nodeKey, { ...def, AIInstruction }),
    );

  return (
    <span
      onMouseEnter={(event) => setName(readFieldName(event.currentTarget))}
      onFocus={(event) => setName(readFieldName(event.currentTarget))}
    >
      <InstructionBox
        text={def.AIInstruction?.trim() ?? ''}
        hint={formatMessage(
          {
            id: 'workspace.templateEdit.fieldGuidanceHint',
            defaultMessage: 'AI guidance for the field “{name}” — not printed',
          },
          { name },
        )}
        guidance
        multiline={false}
        dataComponent="workspace-template-field-guidance"
        onCommit={(typed) => setInstruction(typed || undefined)}
        onRemoveEmpty={() => setInstruction(undefined)}
        onLeave={leave}
      />
    </span>
  );
};

const TemplateFieldView: TemplateFieldWidget = memo(({ nodeKey, def }) => (
  <>
    <FieldBody nodeKey={nodeKey} def={def} />
    {!!def.AIInstruction?.trim() && (
      <FieldGuidance nodeKey={nodeKey} def={def} />
    )}
  </>
));

TemplateFieldView.displayName = 'TemplateFieldView';

type InstructionBoxProps = {
  // What the box shows while it is not typed in.
  text: string;
  hint: string;
  // Guidance, never printed, reads muted.
  guidance: boolean;
  // Shift+Enter starts a new line in it.
  multiline: boolean;
  // A label shown in place of an absent instruction.
  labelled?: boolean;
  focusRequested?: boolean;
  dataComponent: string;
  children?: ReactNode;
  onCommit: (typed: string) => void;
  onRemoveEmpty: () => void;
  onLeave: () => void;
  onFocusTaken?: () => void;
};

// An AI instruction in square brackets, typed inside them: a slot's in the
// colour of an instruction, guidance muted. Enter or Escape gives the
// document back the caret after it, Backspace in an empty box removes it.
const InstructionBox: FC<InstructionBoxProps> = ({
  text,
  hint,
  guidance,
  multiline,
  labelled = false,
  focusRequested = false,
  dataComponent,
  children,
  onCommit,
  onRemoveEmpty,
  onLeave,
  onFocusTaken,
}) => {
  const scope = useWidgetScope();
  const box = useRef<HTMLSpanElement>(null);
  const normalize = (typed: string) =>
    multiline ? typed.trim() : typed.replace(/\s+/g, ' ').trim();

  useLayoutEffect(() => {
    const element = box.current;
    if (element && document.activeElement !== element) {
      element.textContent = text;
    }
  }, [text]);

  useEffect(() => {
    if (focusRequested) {
      onFocusTaken?.();
      box.current?.focus({ preventScroll: true });
    }
    // A request is followed once, when it is made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequested]);

  const commit = () => {
    const typed = normalize(box.current?.textContent ?? '');
    if (typed !== text) {
      onCommit(typed);
    }
  };

  return (
    <span
      className={cn(
        styles.instruction,
        guidance && styles.guidance,
        labelled && styles.instructionLabel,
      )}
      title={hint}
      data-component={dataComponent}
    >
      <span aria-hidden>[</span>
      <span
        ref={box}
        className={styles.instructionText}
        role="textbox"
        aria-label={hint}
        aria-multiline={multiline || undefined}
        tabIndex={scope.editable ? 0 : -1}
        contentEditable={scope.editable}
        suppressContentEditableWarning
        data-component={`${dataComponent}-text`}
        onBlur={commit}
        onPaste={(event) => {
          event.preventDefault();
          const pasted = event.clipboardData.getData('text/plain');
          document.execCommand(
            'insertText',
            false,
            multiline ? pasted : pasted.replace(/\s+/g, ' '),
          );
        }}
        onKeyDown={(event) => {
          const element = event.currentTarget;
          // The box's own line break, kept as one in the text it holds.
          if (event.key === 'Enter' && multiline && event.shiftKey) {
            event.preventDefault();
            document.execCommand('insertText', false, '\n');
          } else if (event.key === 'Enter') {
            event.preventDefault();
            commit();
            onLeave();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            element.textContent = text;
            onLeave();
          }
          if (event.key === 'Backspace' && !element.textContent) {
            event.preventDefault();
            onLeave();
            onRemoveEmpty();
          }
        }}
      />
      <span aria-hidden>]</span>
      {children}
    </span>
  );
};

// An instruction in the document: a slot the AI writes text into, its
// label greyed while it has no instruction, its corner button, while it is
// active, opening its id and code; or its section's guidance, alone under
// the heading: a summary's or an evaluation's field's text is written from
// it.
const InstructionView: TemplateFieldWidget = memo(({ nodeKey, def, role }) => {
  const scope = useWidgetScope();
  const [editor] = useLexicalComposerContext();
  const { formatMessage } = useIntl();
  const active = useIsActive(nodeKey);
  const request = useRequest(def.ID);
  const [open, setOpen] = useState(false);
  const leave = useLeaveBlock(nodeKey);
  const guidance = role === 'section';
  const label =
    def.Kind.case === 'FreeText' ? def.Kind.value.Label?.trim() ?? '' : '';
  const instruction = def.AIInstruction?.trim() ?? '';
  const showsLabel = !guidance && !instruction && !!label;
  const slotHint = formatMessage({
    id: 'workspace.templateEdit.slotHint',
    defaultMessage: 'The AI writes text here',
  });
  const sectionHint = scope.fields
    ? formatMessage({
        id: 'workspace.templateEdit.fieldTextGuidanceHint',
        defaultMessage: 'The AI writes this field’s text from this instruction',
      })
    : formatMessage({
        id: 'workspace.templateEdit.sectionGuidanceHint',
        defaultMessage: 'AI guidance for the whole section — not printed',
      });

  return (
    <InstructionBox
      text={instruction || (showsLabel ? label : '')}
      hint={guidance ? sectionHint : slotHint}
      guidance={guidance}
      multiline={guidance}
      labelled={showsLabel}
      focusRequested={request === 'text'}
      dataComponent={
        guidance
          ? 'workspace-template-section-guidance'
          : 'workspace-template-slot'
      }
      onFocusTaken={() => scope.request.set(null)}
      onCommit={(typed) =>
        editor.update(() =>
          $setTemplateBlockDef(nodeKey, {
            ...def,
            AIInstruction: typed || undefined,
          }),
        )
      }
      onRemoveEmpty={() => editor.update(() => $removeTemplateBlock(nodeKey))}
      onLeave={leave}
    >
      {scope.editable && !guidance && (active || open) && (
        <span className={styles.corner}>
          <SettingsPopover
            portal
            label={formatMessage({
              id: 'workspace.templateEdit.slotSettings',
              defaultMessage: 'Instruction settings',
            })}
            side="top"
            iconSize={12}
            className={styles.cornerButton}
            open={open}
            dataComponent="workspace-template-slot-menu"
            onOpenChange={(next) => {
              setOpen(next);
              scope.pins.set({
                ...scope.pins.get(),
                field: next ? nodeKey : null,
              });
            }}
          >
            <SlotPopupSettings
              block={def}
              onChange={(block) =>
                editor.update(() => $setTemplateBlockDef(nodeKey, block))
              }
            />
          </SettingsPopover>
        </span>
      )}
    </InstructionBox>
  );
});

InstructionView.displayName = 'InstructionView';

const TEMPLATE_FIELD_WIDGETS: TemplateFieldWidgets = {
  field: TemplateFieldView,
  instruction: InstructionView,
};

// What a field of a summary or an evaluation says under its title: what a
// rename does and what keeps the list from saving.
const FieldNotes: FC<{ entry: SectionEntry }> = ({ entry }) => {
  const previousTitle = entry.savedTitle?.trim();
  const renamedFrom =
    previousTitle && previousTitle !== entry.title.trim()
      ? previousTitle
      : undefined;
  const issueText: Record<StructureIssue['kind'], ReactNode> = {
    emptyTitle: (
      <FormattedMessage
        id="workspace.template.emptyFieldTitle"
        defaultMessage="The field has no title. Give it one to save the template."
      />
    ),
    duplicateTitle: (
      <FormattedMessage
        id="workspace.template.duplicateFieldTitle"
        defaultMessage="Another field has the same title. Rename one of them to save the template."
      />
    ),
    duplicateCode: (
      <FormattedMessage
        id="workspace.template.duplicateFieldCode"
        defaultMessage="Another field has the same code “{code}”. Delete one of them to save the template."
        values={{ code: entry.code }}
      />
    ),
    longTitle: (
      <FormattedMessage
        id="workspace.template.longFieldTitle"
        defaultMessage="The title is longer than 200 characters."
      />
    ),
    longInstruction: (
      <FormattedMessage
        id="workspace.template.longFieldInstruction"
        defaultMessage="The AI instruction is longer than 4000 characters."
      />
    ),
    placeholder: (
      <FormattedMessage
        id="workspace.template.fieldPlaceholder"
        defaultMessage="Remove the '{{…}}' placeholder from the title or the AI instruction."
      />
    ),
  };
  if (!renamedFrom && !entry.fieldIssues.length) {
    return null;
  }

  return (
    <span className={styles.fieldNotes}>
      {renamedFrom && (
        <span
          className={cn('p4', styles.fieldRenamed)}
          data-component="workspace-template-field-renamed"
        >
          <Icon className={styles.fieldRenamedIcon} name="info" size={14} />
          <FormattedMessage
            id="workspace.template.renamedField"
            defaultMessage="The model writes this field's answer under its title, and documents show it. It was “{previous}”."
            values={{ previous: renamedFrom }}
          />
        </span>
      )}
      {entry.fieldIssues.map((kind) => (
        <span
          key={kind}
          role="alert"
          className={cn('p4', styles.fieldIssueText)}
        >
          {issueText[kind]}
        </span>
      ))}
    </span>
  );
};

// A section's pop-up: its id, code, whether it is required, whether AI may
// reword its wording, its AI instruction and its removal; a field's of a
// summary or an evaluation its id, code and removal (none before a
// recording, where only titles and instructions change).
const SectionMenu: FC<{
  sectionId: string;
  entry: SectionEntry;
  onOpenChange: (open: boolean) => void;
}> = ({ sectionId, entry, onOpenChange }) => {
  const scope = useWidgetScope();
  const [editor] = useLexicalComposerContext();
  const { formatMessage } = useIntl();
  const update = (patch: Partial<DocumentSectionSpec>) =>
    editor.update(() => $updateTemplateSectionById(sectionId, patch));
  const remove = () => {
    onOpenChange(false);
    editor.update(() => $removeTemplateSection(sectionId));
  };
  const previousTitle = entry.savedTitle?.trim();

  return (
    <SettingsPopover
      portal
      label={formatMessage(
        scope.fields
          ? {
              id: 'workspace.template.fieldSettings',
              defaultMessage: 'Field settings',
            }
          : {
              id: 'workspace.template.sectionSettings',
              defaultMessage: 'Section settings',
            },
      )}
      side="top"
      iconSize={14}
      className={styles.sectionButton}
      dataComponent="workspace-template-section-menu"
      onOpenChange={onOpenChange}
    >
      {scope.fields ? (
        <SectionPopupSettings
          id={sectionId}
          code={entry.code}
          codeHint={
            previousTitle !== undefined ? (
              <FormattedMessage
                id="workspace.template.codeLocked"
                defaultMessage="Generated documents keep this field's text under its code, so the code does not change."
              />
            ) : (
              <FormattedMessage
                id="workspace.template.codeFollowsTitle"
                defaultMessage="A new field's code is made from its title until the version is saved; then it does not change."
              />
            )
          }
          removeLabel={
            <FormattedMessage
              id="workspace.template.deleteField"
              defaultMessage="Delete field"
            />
          }
          onRemove={remove}
        />
      ) : (
        <SectionPopupSettings
          id={sectionId}
          code={entry.code}
          required={entry.required}
          rewrite={entry.rewrite}
          removeLabel={
            <FormattedMessage
              id="workspace.template.deleteSection"
              defaultMessage="Delete section"
            />
          }
          onRequired={() => update({ Required: !entry.required })}
          onRewrite={() => update({ AllowAITextRewrite: !entry.rewrite })}
          onRemove={remove}
        />
      )}
    </SettingsPopover>
  );
};

type Place = {
  top: number;
  left: number;
  bottom: number;
  // Where the block's text ends.
  endTop: number;
  endBottom: number;
  endLeft: number;
};

type ToolsTarget = {
  kind: 'line' | 'section';
  id: string;
  key: NodeKey;
  place: Place;
};

// Where a block sits in the page, and where its last row of text ends.
const measure = (element: HTMLElement, frame: HTMLElement): Place => {
  const box = element.getBoundingClientRect();
  const origin = frame.getBoundingClientRect();
  const range = document.createRange();
  range.selectNodeContents(element);
  const rows =
    typeof range.getClientRects === 'function'
      ? Array.from(range.getClientRects()).filter(
          (rect) =>
            rect.width > 0 &&
            rect.top >= box.top &&
            rect.bottom <= box.bottom + 1,
        )
      : [];
  const end = rows.reduce<DOMRect | null>(
    (last, rect) =>
      !last ||
      rect.top > last.top + 1 ||
      (rect.top >= last.top - 1 && rect.right > last.right)
        ? rect
        : last,
    null,
  );
  return {
    top: box.top - origin.top,
    left: box.left - origin.left,
    bottom: box.bottom - origin.top,
    endTop: (end?.top ?? box.top) - origin.top,
    endBottom: (end?.bottom ?? box.bottom) - origin.top,
    endLeft: (end?.right ?? box.left) - origin.left,
  };
};

const $readToolsTarget = (
  editor: LexicalEditor,
  key: NodeKey | null,
  frame: HTMLElement,
): ToolsTarget | null => {
  const node = key ? $getNodeByKey(key) : null;
  const element = key ? editor.getElementByKey(key) : null;
  if (!node || !element) {
    return null;
  }
  if ($isTemplateHeadingNode(node)) {
    const id = $readTemplateSection(node)?.ID;
    return id
      ? {
          kind: 'section',
          id,
          key: node.getKey(),
          place: measure(element, frame),
        }
      : null;
  }
  const lineId =
    $isTemplateParagraphNode(node) &&
    !node.getChildren().some($isSectionGuidance)
      ? $readTemplateLine(node)?.ID
      : undefined;
  return lineId
    ? {
        kind: 'line',
        id: lineId,
        key: node.getKey(),
        place: measure(element, frame),
      }
    : null;
};

const LineTools: FC<{ target: ToolsTarget }> = ({ target }) => {
  const scope = useWidgetScope();
  const entry = useNoteBlockEntry(scope.lines, target.id);
  const linkJump = useLinkJump();
  const linkEditing = useTemplateLineLinkEditing(
    TEMPLATE_LINK_ADAPTER,
    target.id,
  );
  const { place } = target;
  const editsLinks = scope.editable && !scope.fields && !scope.structureLocked;
  const openers: NamedNoteLinkOpener[] = listShownLineOpeners(
    (entry?.openers ?? []).map((opener) => ({
      ...opener,
      label: nameNoteLinkOpener(scope.latest.current, opener),
    })),
  );

  return (
    <>
      <span
        className={styles.lineEnd}
        style={{ top: place.endTop, left: place.endLeft }}
        data-note-line-id={target.id}
        data-caret-line=""
      >
        <LinkedLineMark
          openers={openers}
          dataComponent="workspace-template-linked-label"
          linkEditing={editsLinks ? linkEditing : undefined}
          onSelect={linkJump.toOption}
          onMenuOpenChange={(open) =>
            scope.pins.set({
              ...scope.pins.get(),
              block: open ? target.key : null,
            })
          }
        />
      </span>
      <span
        className={styles.notes}
        style={{ top: place.bottom + 2, left: place.left }}
      >
        <TemplateIssues issues={entry?.issues ?? NO_ISSUES} />
      </span>
    </>
  );
};

const SectionTools: FC<{ target: ToolsTarget }> = ({ target }) => {
  const scope = useWidgetScope();
  const entry = useNoteBlockEntry(scope.sections, target.id);
  const { place } = target;
  if (!entry) {
    return null;
  }

  return (
    <>
      {scope.editable && !scope.structureLocked && (
        <span
          className={styles.sectionEnd}
          style={{
            top: (place.endTop + place.endBottom) / 2,
            left: place.endLeft,
          }}
        >
          <SectionMenu
            sectionId={target.id}
            entry={entry}
            onOpenChange={(open) =>
              scope.pins.set({
                ...scope.pins.get(),
                block: open ? target.key : null,
              })
            }
          />
        </span>
      )}
      <span
        className={styles.notes}
        style={{ top: place.bottom + 2, left: place.left }}
      >
        <FieldNotes entry={entry} />
        <TemplateIssues issues={entry.issues} />
      </span>
    </>
  );
};

// The tools of the line or heading under the pointer or the caret, over the
// text in one layer: they never take room in it, so nothing moves.
const ToolsOverlay: FC<{
  frame: RefObject<HTMLDivElement>;
  onPointer: (inside: boolean) => void;
}> = ({ frame, onPointer }) => {
  const scope = useWidgetScope();
  const [editor] = useLexicalComposerContext();
  const blockKey = useStoreValue(scope.active, (active) => active.block);
  const [target, setTarget] = useState<ToolsTarget | null>(null);

  useLayoutEffect(() => {
    const read = () => {
      const page = frame.current;
      const next = page
        ? editor.read(() => $readToolsTarget(editor, blockKey, page))
        : null;
      setTarget((current) => (isEqual(current, next) ? current : next));
    };
    const pending = { frame: 0 };
    const schedule = () => {
      cancelAnimationFrame(pending.frame);
      pending.frame = requestAnimationFrame(read);
    };
    read();
    window.addEventListener('resize', schedule);
    const removeUpdates = editor.registerUpdateListener(schedule);
    return () => {
      cancelAnimationFrame(pending.frame);
      window.removeEventListener('resize', schedule);
      removeUpdates();
    };
  }, [editor, blockKey, frame]);

  if (!target) {
    return null;
  }

  return (
    <div
      className={styles.overlay}
      data-component="workspace-template-overlay"
      onPointerEnter={() => onPointer(true)}
      onPointerLeave={() => onPointer(false)}
    >
      {target.kind === 'line' ? (
        <LineTools key={target.id} target={target} />
      ) : (
        <SectionTools key={target.id} target={target} />
      )}
    </div>
  );
};

type Hover = { field: NodeKey | null; block: NodeKey | null };

// Follows the pointer, the focus and the caret, and tells the widgets which
// field and which line or heading are active.
const ActivePlugin: FC<{
  pointerInTools: MutableRefObject<(inside: boolean) => void>;
}> = ({ pointerInTools }) => {
  const scope = useWidgetScope();
  const [editor] = useLexicalComposerContext();
  const { active, pins } = scope;

  useEffect(() => {
    const state = {
      hover: { field: null, block: null } as Hover,
      focusField: null as NodeKey | null,
      caret: { field: null, block: null } as Hover,
      leaving: 0,
      // React reports the pointer entering the tools before the browser
      // reports it leaving the document, so that leave must not hide them.
      inTools: false,
      selected: null as HTMLElement | null,
    };
    const publish = () => {
      const pinned = pins.get();
      active.set({
        field:
          pinned.field ??
          state.hover.field ??
          state.focusField ??
          state.caret.field,
        block: pinned.block ?? state.hover.block ?? state.caret.block,
        held: pinned.field ?? state.focusField ?? state.caret.field,
      });
    };
    const keyOf = (element: Element | null) =>
      element
        ? editor.read(
            () => $getNearestNodeFromDOMNode(element)?.getKey() ?? null,
          )
        : null;
    const onPointerMove = (event: PointerEvent) => {
      window.clearTimeout(state.leaving);
      state.inTools = false;
      const root = editor.getRootElement();
      const target = event.target;
      if (!(target instanceof Element) || !root || target === root) {
        return;
      }
      const hover = {
        field: keyOf(target.closest('[data-lexical-decorator]')),
        block: keyOf(target.closest('[data-lexical-editor] > *')),
      };
      if (!isEqual(hover, state.hover)) {
        state.hover = hover;
        publish();
      }
    };
    const leave = () => {
      window.clearTimeout(state.leaving);
      if (state.inTools) {
        return;
      }
      state.leaving = window.setTimeout(() => {
        state.hover = { field: null, block: null };
        publish();
      }, 200);
    };
    pointerInTools.current = (inside) => {
      state.inTools = inside;
      if (inside) {
        window.clearTimeout(state.leaving);
      } else {
        leave();
      }
    };
    // A click on a field's own text, not on a box or a button in it,
    // selects the field.
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      const field =
        target instanceof Element && target.closest('[data-template-field]');
      const control =
        field && target.closest('input, button, [contenteditable="true"]');
      const key =
        field && editor.isEditable() && !field.contains(control || null)
          ? keyOf(field)
          : null;
      if (key) {
        editor.update(() => $selectTemplateBlock(key));
      }
    };
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      state.focusField =
        target instanceof Element
          ? keyOf(target.closest('[data-lexical-decorator]'))
          : null;
      publish();
    };
    const onFocusOut = (event: FocusEvent) => {
      const root = editor.getRootElement();
      const next = event.relatedTarget;
      if (!(next instanceof Node) || !root?.contains(next)) {
        state.focusField = null;
        state.caret = { field: null, block: null };
        publish();
      }
    };
    const readCaret = () => {
      const selection = $getSelection();
      if ($isNodeSelection(selection)) {
        const [node] = selection.getNodes();
        return {
          field:
            $isTemplateFieldNode(node) || $isTemplateInstructionNode(node)
              ? node.getKey()
              : null,
          block: node?.getTopLevelElement()?.getKey() ?? null,
        };
      }
      return {
        field: null,
        block: $isRangeSelection(selection) ? $readCaretBlockKey() : null,
      };
    };
    const markSelected = (key: NodeKey | null) => {
      const element = key ? editor.getElementByKey(key) : null;
      if (element !== state.selected) {
        state.selected?.removeAttribute('data-selected');
        element?.setAttribute('data-selected', '');
        state.selected = element;
      }
    };
    const removePins = pins.subscribe(publish);
    const removeRoot = editor.registerRootListener((root, previous) => {
      previous?.removeEventListener('pointermove', onPointerMove);
      previous?.removeEventListener('pointerleave', leave);
      previous?.removeEventListener('click', onClick);
      previous?.removeEventListener('focusin', onFocusIn);
      previous?.removeEventListener('focusout', onFocusOut);
      root?.addEventListener('pointermove', onPointerMove);
      root?.addEventListener('pointerleave', leave);
      root?.addEventListener('click', onClick);
      root?.addEventListener('focusin', onFocusIn);
      root?.addEventListener('focusout', onFocusOut);
    });
    return mergeRegister(
      removePins,
      removeRoot,
      editor.registerUpdateListener(({ editorState }) => {
        const root = editor.getRootElement();
        const caret = editorState.read(readCaret);
        markSelected(caret.field);
        const focused =
          !!root &&
          (root === document.activeElement ||
            root.contains(document.activeElement));
        const next = focused ? caret : state.caret;
        if (!isEqual(next, state.caret)) {
          state.caret = next;
          publish();
        }
      }),
      () => window.clearTimeout(state.leaving),
    );
  }, [editor, active, pins, pointerInTools]);

  return null;
};

// The marks a line or heading keeps in sight without taking room: what it
// cannot be saved with, and an evaluation field AI scores.
const MarksPlugin: FC<{
  issueLines: string[];
  issueSections: string[];
  scoredSections: string[];
  scoredLabel: string;
}> = ({ issueLines, issueSections, scoredSections, scoredLabel }) => {
  const [editor] = useLexicalComposerContext();
  const marks = { issueLines, issueSections, scoredSections, scoredLabel };
  // The marks change rarely; the effect follows what they hold.
  const held = JSON.stringify(marks);

  useEffect(() => {
    const read: typeof marks = JSON.parse(held);
    const marked =
      read.issueLines.length +
      read.issueSections.length +
      read.scoredSections.length;
    const apply = () => {
      const root = editor.getRootElement();
      root
        ?.querySelectorAll<HTMLElement>('[data-line-id]')
        .forEach((line) =>
          line.toggleAttribute(
            'data-template-issue',
            read.issueLines.includes(line.dataset.lineId ?? ''),
          ),
        );
      root
        ?.querySelectorAll<HTMLElement>('[data-section-title]')
        .forEach((heading) => {
          const id = heading.dataset.sectionTitle ?? '';
          heading.toggleAttribute(
            'data-template-issue',
            read.issueSections.includes(id),
          );
          markTemplateHeadingTag(
            heading,
            read.scoredSections.includes(id) ? read.scoredLabel : undefined,
          );
        });
    };
    apply();
    // Lexical draws a line or heading anew as the text changes; one with
    // marks gets them back.
    return marked
      ? editor.registerUpdateListener(({ dirtyElements }) => {
          if (dirtyElements.has('root')) {
            apply();
          }
        })
      : undefined;
  }, [editor, held]);

  return null;
};

// What the toolbar inserts at the caret.
export type TemplateInsertKind = Extract<
  NoteBlockPaletteKind,
  'options' | 'teeth' | 'date' | 'number' | 'table'
>;

// What the page's toolbar does to the document: its undo and redo, the
// caret's block made a heading, a subheading or text, an AI instruction and
// a field put at the caret. A summary's or an evaluation's field is its
// title and instruction: a heading adds a field, the instruction brackets
// the caret field's own, and it takes no lines.
export type TemplateDocumentControls = DocumentHistory & {
  // A section or a field of a summary is added: not while the structure is
  // locked.
  canAddSection: boolean;
  // Subheadings, text lines and the fields in them are added.
  canAddLines: boolean;
  // The selection or the caret holds a line to link.
  canLink: boolean;
  heading: () => void;
  subheading: () => void;
  text: () => void;
  instruction: () => void;
  insert: (kind: TemplateInsertKind) => void;
  link: () => void;
};

const ControlsPlugin: FC<{
  history: TemplateEditorHistory;
  editable: boolean;
  fields: boolean;
  structureLocked: boolean;
  newSectionTitle: string;
  newFieldName: string;
  onRequest: (request: WidgetRequest) => void;
  onControls: (controls: TemplateDocumentControls) => void;
}> = ({
  history,
  editable,
  fields,
  structureLocked,
  newSectionTitle,
  newFieldName,
  onRequest,
  onControls,
}) => {
  const [editor] = useLexicalComposerContext();
  const handlers = useRef({
    onControls,
    onRequest,
    newSectionTitle,
    newFieldName,
  });
  // The line or heading the caret was last in: a tool reached by the
  // keyboard takes the focus, and what it adds goes there.
  const lastBlock = useRef<NodeKey | null>(null);
  const [holdsLines, setHoldsLines] = useState(false);
  const canAddSection = editable && !structureLocked;
  const canAddLines = canAddSection && !fields;
  const canLink = canAddLines && holdsLines;

  useLayoutEffect(() => {
    handlers.current = { onControls, onRequest, newSectionTitle, newFieldName };
  });

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(({ editorState }) => {
          const key = editorState.read($readCaretBlockKey);
          if (key) {
            lastBlock.current = key;
          }
          setHoldsLines(
            editorState.read(() => $readSelectedTemplateLineIds().length > 0),
          );
        }),
        editor.registerCommand(
          TEMPLATE_INSTRUCTION_TYPED_COMMAND,
          (blockId) => {
            handlers.current.onRequest({ blockId, kind: 'text' });
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
      ),
    [editor],
  );

  useEffect(() => {
    const turnLine = (subheading: boolean) => {
      editor.update(() =>
        $turnCaretIntoTemplateLine(subheading, lastBlock.current),
      );
      editor.focus();
    };
    handlers.current.onControls({
      ...history,
      canAddSection,
      canAddLines,
      canLink,
      undo: () => editor.dispatchCommand(UNDO_COMMAND, undefined),
      redo: () => editor.dispatchCommand(REDO_COMMAND, undefined),
      heading: () => {
        const title = handlers.current.newSectionTitle;
        editor.update(() =>
          fields
            ? $insertTemplateFieldSection(
                createListField(title),
                lastBlock.current,
              )
            : $turnCaretIntoTemplateHeading(
                { ...createNoteSection(title), Lines: [] },
                lastBlock.current,
              ),
        );
        editor.focus();
      },
      subheading: () => turnLine(true),
      text: () => turnLine(false),
      instruction: () => {
        if (fields) {
          const field = { instructionId: null as string | null };
          editor.update(() => {
            field.instructionId = $bracketTemplateFieldInstruction(
              lastBlock.current,
            );
          });
          if (field.instructionId) {
            handlers.current.onRequest({
              blockId: field.instructionId,
              kind: 'text',
            });
          }
          return;
        }
        const inserted = { def: createTemplateSlotDef() };
        editor.update(() => {
          inserted.def = createTemplateSlotDef($readSelectedTemplateText());
          $insertTemplateBlockAtCaret(inserted.def, lastBlock.current);
        });
        if (inserted.def.AIInstruction) {
          editor.focus();
        } else {
          handlers.current.onRequest({
            blockId: inserted.def.ID,
            kind: 'text',
          });
        }
      },
      insert: (kind) => {
        const block = createNoteBlock(kind);
        const inserted = { named: false };
        editor.update(() => {
          inserted.named = $insertTemplateBlockAtCaret(
            block,
            lastBlock.current,
            handlers.current.newFieldName,
          ).named;
        });
        if (!inserted.named && kind === 'options') {
          handlers.current.onRequest({ blockId: block.ID, kind: 'options' });
        } else if (!inserted.named && kind === 'table') {
          handlers.current.onRequest({ blockId: block.ID, kind: 'settings' });
        } else {
          editor.focus();
        }
      },
      link: () => {
        editor.dispatchCommand(START_TEMPLATE_LINK_COMMAND, undefined);
      },
    });
  }, [editor, history, fields, canAddSection, canAddLines, canLink]);

  return null;
};

const FocusPlugin: FC<{ focus: TemplateDocumentFocus | undefined }> = ({
  focus,
}) => {
  const jump = useCaretJump();

  useEffect(() => {
    if (focus) {
      const [target] = jump(() => [
        $selectTemplateLine(focus.targetId) ??
          $selectTemplateSection(focus.targetId),
      ]);
      target?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }
    // A request is followed once, when it is made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.at]);

  return null;
};

export type TemplateDocumentChange = {
  template: DocumentTemplate;
  // What the document cannot save as it is.
  issues: TemplateIssue[];
};

type Props = {
  // Read once: a key starts the editor on another template.
  template: DocumentTemplate;
  // The version the edit started from.
  source: DocumentTemplate;
  lists: NoteList[];
  // What scribe refused in the last save.
  issues: TemplateIssue[];
  editable: boolean;
  focus?: TemplateDocumentFocus;
  ariaLabel: string;
  // The title of a section or field the toolbar adds, in the template's
  // language.
  newSectionTitle?: string;
  // The name a field the toolbar puts at a line's start gets to type over,
  // in the template's language.
  newFieldName?: string;
  className?: string;
  // Plugins inside the editor.
  children?: ReactNode;
  // A summary's or an evaluation's fields: each section a field with its
  // title and AI instruction, and no lines.
  fields?: boolean;
  // Only the fields' titles and instructions are typed: none is added,
  // removed or moved, and nothing else is set.
  structureLocked?: boolean;
  onChange: (change: TemplateDocumentChange) => void;
  onControls: (controls: TemplateDocumentControls) => void;
};

// A template edited as one rich-text document: a section is a heading, a
// line a paragraph, an AI instruction is typed in brackets, and a field sits
// in the sentence in its frame. The tools of what is under the pointer or
// the caret float over the text. The template is read back from the
// document once typing pauses.
export const TemplateDocumentEditor: FC<Props> = ({
  template,
  source,
  lists,
  issues,
  editable,
  focus,
  ariaLabel,
  newSectionTitle = '',
  newFieldName = '',
  className,
  children,
  fields = false,
  structureLocked = false,
  onChange,
  onControls,
}) => {
  const { formatMessage } = useIntl();
  const [snapshot, setSnapshot] = useState(template);
  const [documentIssues, setDocumentIssues] = useState<TemplateIssue[]>([]);
  const [history, setHistory] = useState<TemplateEditorHistory>({
    canUndo: false,
    canRedo: false,
  });
  const latest = useRef(snapshot);
  const page = useRef<HTMLDivElement>(null);
  const pointerInTools = useRef<(inside: boolean) => void>(() => undefined);
  const [stores] = useState(() => ({
    blocks: createNoteBlockStore<BlockEntry>({ isSame: isEqual }),
    lines: createNoteBlockStore<LineEntry>({ isSame: isEqual }),
    options: createNoteBlockStore<string[]>({ isSame: isEqual }),
    sections: createNoteBlockStore<SectionEntry>({ isSame: isEqual }),
    active: createValueStore<ActiveTarget>({
      field: null,
      block: null,
      held: null,
    }),
    pins: createValueStore<ActivePins>({ field: null, block: null }),
    request: createValueStore<WidgetRequest>(null),
  }));
  const headingPlaceholder = formatMessage({
    id: 'workspace.templateEdit.heading',
    defaultMessage: 'Heading',
  });
  const scoredLabel = formatMessage({
    id: 'workspace.preview.scored',
    defaultMessage: 'Scored 1–5',
  });
  const placed = placeTemplateIssues({
    template: snapshot,
    issues: [...issues, ...documentIssues],
    shownLineIds: listLineIds(snapshot),
  });
  const lineEntries = listLineEntries(snapshot, placed);
  const sectionEntries = listSectionEntries(snapshot, source, placed, fields);

  useLayoutEffect(() => {
    latest.current = snapshot;
    stores.blocks.set(listBlockEntries(snapshot, placed));
    stores.lines.set(lineEntries);
    stores.options.set(listOptionEntries(snapshot));
    stores.sections.set(sectionEntries);
    // The placed issues follow the snapshot and the issues.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores, snapshot, source, issues, documentIssues, fields]);

  useEffect(
    () => () => {
      stores.blocks.cancel();
      stores.lines.cancel();
      stores.options.cancel();
      stores.sections.cancel();
    },
    [stores],
  );

  // A scope that changes renders every widget again, so it changes only
  // with what every widget shows.
  const scope = useMemo<WidgetScope>(
    () => ({
      ...stores,
      latest,
      lists,
      editable,
      fields,
      structureLocked,
    }),
    [stores, lists, editable, fields, structureLocked],
  );

  const change = (exported: TemplateDocumentExport) => {
    const applied = applyTemplateDocument(template, exported);
    const next = fields
      ? withFieldCodes(restoreStoredFieldOrder(applied, template), source)
      : applied;
    setSnapshot(next);
    setDocumentIssues(exported.issues);
    onChange({ template: next, issues: exported.issues });
  };

  const issueLines = Object.entries(lineEntries).flatMap(([id, entry]) =>
    entry.issues.length ? [id] : [],
  );
  const issueSections = Object.entries(sectionEntries).flatMap(([id, entry]) =>
    entry.issues.length || entry.fieldIssues.length ? [id] : [],
  );
  const scoredSections = Object.entries(sectionEntries).flatMap(
    ([id, entry]) => (entry.scored ? [id] : []),
  );

  return (
    <WidgetScopeContext.Provider value={scope}>
      <TemplateIssues issues={placed.unplaced} />
      <div
        ref={page}
        className={styles.page}
        style={
          {
            '--template-heading-placeholder':
              JSON.stringify(headingPlaceholder),
          } as CSSProperties
        }
      >
        <TemplateEditor
          sections={
            fields
              ? showTranscriptQualityFirst(
                  template.type,
                  template.sections,
                  (section) => section.Code,
                )
              : template.sections
          }
          fields={fields}
          structureLocked={structureLocked}
          widgets={TEMPLATE_FIELD_WIDGETS}
          editable={editable}
          ariaLabel={ariaLabel}
          className={cn(
            styles.editor,
            fields && styles.fieldsEditor,
            className,
          )}
          createSection={fields ? createListField : createNoteSection}
          onChange={change}
          onHistoryChange={setHistory}
        >
          <ActivePlugin pointerInTools={pointerInTools} />
          <ToolsOverlay
            frame={page}
            onPointer={(inside) => pointerInTools.current(inside)}
          />
          <MarksPlugin
            issueLines={issueLines}
            issueSections={issueSections}
            scoredSections={scoredSections}
            scoredLabel={scoredLabel}
          />
          <ControlsPlugin
            history={history}
            editable={editable}
            fields={fields}
            structureLocked={structureLocked}
            newSectionTitle={newSectionTitle}
            newFieldName={newFieldName}
            onRequest={stores.request.set}
            onControls={onControls}
          />
          <FocusPlugin focus={focus} />
          {children}
        </TemplateEditor>
      </div>
    </WidgetScopeContext.Provider>
  );
};
